import { toast } from 'react-toastify';
import useDeleteCampaign from './useDeleteCampaign';
import useStopCampaign from './useStopCampaign';
import useConfirm from '../useConfirm';
import useLoadingModal from '../global/useLoadingModal';
import {
  CampaignActionHandlers,
  MarketingCampaignWithSource,
} from '../../types/marketing';
import { Optional } from '../../types/global';

interface Options {
  onSelectCampaign?: (campaignId: string) => void;
  // Edit/Duplicate route through this callback — typically wired to the
  // CampaignEditorProvider's `open` so the editor mounts in one place.
  openEditor: (payload: {
    campaign: Optional<MarketingCampaignWithSource, 'id'>;
  }) => void;
}

function useCampaignActions({ onSelectCampaign, openEditor }: Options) {
  const confirm = useConfirm();
  const { openLoadingModal, closeLoadingModal } = useLoadingModal();

  const { mutate: deleteCampaign, isPending: isDeleting } = useDeleteCampaign({
    onMutate: () => {
      openLoadingModal({
        header: 'Deleting campaign',
        message: 'Please wait while the campaign is being deleted',
      });
    },
    onSettled: () => closeLoadingModal(),
  });
  const { mutate: stopCampaign, isPending: isStopping } = useStopCampaign({
    onMutate: () => {
      openLoadingModal({
        header: 'Stopping campaign',
        message: 'Please wait while the campaign is being stopped',
      });
    },
    onSettled: () => closeLoadingModal(),
    onError: () => {
      toast.error('Failed to stop campaign');
    },
  });

  const onDeleteCampaign = async (campaignId: string) => {
    const confirmed = await confirm({
      title: 'Delete campaign',
      message:
        'Are you sure you want to delete this campaign? This action cannot be undone.',
      positiveText: 'Delete',
    });
    if (!confirmed) return;
    deleteCampaign(campaignId);
  };

  const onStopCampaign = async (campaignId: string) => {
    const confirmed = await confirm({
      title: 'Stop campaign',
      message:
        'Are you sure you want to stop this campaign? This action cannot be undone. If you purchased credits for this campaign, the credits will be available for future campaigns.',
      positiveText: 'Stop',
    });
    if (!confirmed) return;
    stopCampaign(campaignId);
  };

  const handlers: CampaignActionHandlers = {
    onEditCampaign: campaign => openEditor({ campaign }),
    onDuplicateCampaign: campaign =>
      openEditor({ campaign: { ...campaign, id: undefined } }),
    onStopCampaign,
    onDeleteCampaign,
    onSelectCampaign,
  };

  return {
    handlers,
    isPending: isDeleting || isStopping,
  };
}

export default useCampaignActions;
